import React, { useEffect, useState } from 'react';
import { Footer } from '@appsvio/config-page-layout';
import { ErrorMessage } from '@atlaskit/form';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { router, view } from '@forge/bridge';

import { DOCS_URL } from '../../../common/constants';
import { ABSOLUTE } from '../../../components/config/constants';
import { FieldConfigContent, PositionedElement } from '../../../components/config/styles';
import { useIsLicenseActive } from '../../../hooks/useIsLicenseActive';
import { LicenseBanner } from '../../../utils/LicenseBanner';
import { InfoText, Label } from '../../Styled';
import LoadTemplate from '../config/components/LoadTemplate';
import TestingArea from '../config/components/TestingArea';

import { ContentWrapper, PatternSection, Wrapper } from './indexStyled';
import { jiraExpressionExamples } from './jiraExpressionExamples';
const JIRAEXP_CHEATSHEET_URL = 'https://developer.atlassian.com/cloud/jira/software/jira-expressions/';

type FieldConfig = {
    jiraexp: string;
};

const Configuration: React.FC = () => {
    const [jiraExpressionPattern, setJiraExpressionPattern] = useState('');
    const [fieldConfig, setFieldConfig] = useState<FieldConfig>({ jiraexp: '' });
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [isFormInvalid, setIsFormInvalid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { isLicenseActive } = useIsLicenseActive();

    const handleJiraExpTemplateChange = (jiraexp: string, templateName: string) => {
        setJiraExpressionPattern(jiraexp);
        setIsFormInvalid(false);
        setSelectedTemplate(templateName);
    };

    const determineConfiguredTemplateName = (jiraexp: string) => {
        const matchedTemplate = jiraExpressionExamples.find(exampleItem => exampleItem.jiraexp === jiraexp);

        matchedTemplate && setSelectedTemplate(matchedTemplate.label);
    };

    const handleCancel = () => {
        setJiraExpressionPattern(fieldConfig.jiraexp);
        setIsFormInvalid(false);
        determineConfiguredTemplateName(fieldConfig.jiraexp);
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        setIsFormInvalid(false);
        if (jiraExpressionPattern) {
            await view.submit({ configuration: { jiraexp: jiraExpressionPattern } });
        } else {
            setIsFormInvalid(true);
            setIsLoading(false);
        }
    };
    useEffect(() => {
        const getContext = async () => {
            const context = await view.getContext();
            if (context.extension.configuration) {
                const configuration = context.extension.configuration as FieldConfig;
                setJiraExpressionPattern(configuration.jiraexp);
                setFieldConfig(configuration);
                determineConfiguredTemplateName(configuration.jiraexp);
            }
        };
        void getContext();
    }, []);
    return (
        <LicenseBanner isLicenseValid={isLicenseActive}>
            <Wrapper>
                {isLoading && (
                    <PositionedElement position={ABSOLUTE}>
                        <Spinner />
                    </PositionedElement>
                )}
                <FieldConfigContent isLicenseValid={isLicenseActive}>
                    <ContentWrapper>
                        <div>
                            <Label
                                isRequired
                                bottomMargin={4}
                            >
                                Jira Expression
                            </Label>
                            <PatternSection>
                                <Textfield
                                    value={jiraExpressionPattern}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setIsFormInvalid(false);
                                        setJiraExpressionPattern(e.target.value);
                                    }}
                                    placeholder='Enter pattern or load template'
                                    testId='jira-expression-pattern'
                                    pattern={jiraExpressionPattern}
                                />
                                <LoadTemplate
                                    onJiraExpressionPatternTemplateChange={handleJiraExpTemplateChange}
                                    selectedTemplate={selectedTemplate}
                                />
                            </PatternSection>
                            {isFormInvalid && <ErrorMessage>This field cannot be empty.</ErrorMessage>}
                            <InfoText topMargin={4}>
                                Read more about Jira expression syntax{' '}
                                <a onClick={() => router.open(JIRAEXP_CHEATSHEET_URL)}>in this cheatsheet</a>. You can find more
                                examples <a onClick={() => router.open(DOCS_URL)}>in our documentation</a>.
                            </InfoText>
                        </div>
                        <TestingArea jiraExpressionPatternToTest={jiraExpressionPattern} />
                    </ContentWrapper>
                </FieldConfigContent>
                <Footer
                    hasFixedContent
                    showTopSeparator
                    styling={{
                        position: 'absolute',
                        bottom: 0,
                    }}
                    fixedContentProps={{
                        linkOpener: () => router.open(DOCS_URL),
                        isSubmitting: isLoading,
                        onCancel: () => handleCancel(),
                        onSubmit: () => handleSubmit(),
                        cancelButtonText: 'Cancel',
                        isSubmitDisabled: isFormInvalid,
                        submitButtonText: 'Save field',
                    }}
                />
            </Wrapper>
        </LicenseBanner>
    );
};
export default Configuration;
